import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { fetchUserPreferences, saveUserPreference } from '../lib/userPreferences';
import { onSignOut } from '../lib/signOutReset';
import {
  type SleepEntry,
  type CycleType,
  DEFAULT_TARGET_MINUTES,
  clampTargetMinutes,
  getCycle,
  sumSleepXP,
  sleepStreak,
  entryMinutes,
} from '../utils/sleep';
import { toLocalYMD, shiftYMD } from '../utils/hydration';

const HISTORY_DAYS = 60;

interface SleepRow {
  log_date: string;
  bedtime: string | null;
  wake_time: string | null;
  quality: number | null;
}

function rowToEntry(row: SleepRow): SleepEntry {
  return { date: row.log_date, bedtime: row.bedtime ?? '', wakeTime: row.wake_time ?? '', quality: row.quality ?? null };
}

interface SleepStore {
  todayDate: string;
  today: SleepEntry | null;
  history: SleepEntry[];
  cycleType: CycleType;
  targetMinutes: number;

  isLoading: boolean;
  isInitialized: boolean;
  initializedUserId: string | null;

  initialize: () => Promise<void>;
  logSleep: (bedtime: string, wakeTime: string, quality: number | null) => Promise<void>;
  setCycleType: (cycle: CycleType) => void;
  setTargetMinutes: (m: number) => void;

  getSleepXP: () => number;
  getStreak: () => number;
  getLastNightMinutes: () => number;
  clearStore: () => void;
}

function withTodayInHistory(history: SleepEntry[], today: SleepEntry): SleepEntry[] {
  const others = history.filter(d => d.date !== today.date);
  return [...others, today].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

export const useSleepStore = create<SleepStore>((set, get) => ({
  todayDate: toLocalYMD(new Date()),
  today: null,
  history: [],
  cycleType: 'mono',
  targetMinutes: DEFAULT_TARGET_MINUTES,

  isLoading: false,
  isInitialized: false,
  initializedUserId: null,

  initialize: async () => {
    if (get().isLoading) return;
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== epoch) return;
    if (get().initializedUserId === user.id) return;

    set({ isLoading: true });
    try {
      const prefs = await fetchUserPreferences();
      const row = prefs?.row;
      const cycleType = (row?.sleep_cycle_type as CycleType) || 'mono';
      const targetMinutes = row?.sleep_target_minutes != null ? clampTargetMinutes(Number(row.sleep_target_minutes)) : DEFAULT_TARGET_MINUTES;

      const todayStr = toLocalYMD(new Date());
      const since = shiftYMD(todayStr, -HISTORY_DAYS);
      const { data, error } = await supabase
        .from('sleep_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', since)
        .order('log_date', { ascending: true });
      // Signed out while reading: the next account must not get this one's nights.
      if (at !== epoch) return;
      if (error) throw error;

      const rows = (data ?? []) as SleepRow[];
      const history = rows.map(rowToEntry);
      const today = history.find(e => e.date === todayStr) ?? null;

      set({ cycleType, targetMinutes, todayDate: todayStr, today, history, isLoading: false, isInitialized: true, initializedUserId: user.id });
    } catch (err) {
      if (at !== epoch) return; // the next account's load may be running: leave its flag alone
      console.error('Error initializing sleep:', err);
      set({ isLoading: false });
    }
  },

  logSleep: async (bedtime, wakeTime, quality) => {
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== epoch) return;
    const todayStr = toLocalYMD(new Date());
    const entry: SleepEntry = { date: todayStr, bedtime, wakeTime, quality };

    set(state => ({ todayDate: todayStr, today: entry, history: withTodayInHistory(state.history, entry) }));

    try {
      const { error } = await supabase.from('sleep_logs').upsert(
        { user_id: user.id, log_date: todayStr, bedtime, wake_time: wakeTime, quality, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,log_date' }
      );
      if (error) throw error;
    } catch (err) {
      console.error('Error logging sleep:', err);
    }
  },

  setCycleType: (cycle) => {
    // Picking a cycle suggests its nightly target (still user-editable).
    const targetMinutes = getCycle(cycle).targetMinutes;
    set({ cycleType: cycle, targetMinutes });
    void saveUserPreference({ sleep_cycle_type: cycle, sleep_target_minutes: targetMinutes });
  },

  setTargetMinutes: (m) => {
    const targetMinutes = clampTargetMinutes(m);
    set({ targetMinutes });
    void saveUserPreference({ sleep_target_minutes: targetMinutes });
  },

  getSleepXP: () => sumSleepXP(get().history, get().targetMinutes),
  getStreak: () => sleepStreak(get().history, get().todayDate, get().targetMinutes, shiftYMD),
  getLastNightMinutes: () => entryMinutes(get().today),

  /**
   * Back to empty — run on sign-out (lib/signOutReset). No `persist`, but a sign-out does
   * not reload the page: without this the nights and the goal stayed for the next account.
   */
  clearStore: () => {
    epoch += 1;
    set({
      todayDate: toLocalYMD(new Date()),
      today: null,
      history: [],
      cycleType: 'mono',
      targetMinutes: DEFAULT_TARGET_MINUTES,
      isLoading: false,
      isInitialized: false,
      initializedUserId: null,
    });
  },
}));

onSignOut(() => useSleepStore.getState().clearStore());
